import '@yandex-int/messenger.widget/lib/ui/popup.css';

import { ClientApi } from '@yandex-int/messenger.sdk';
import {
    Block,
    presets,
    Widget,
    YandexSingleChatConfig,
    yandexUnreadCounterFactory,
} from '@yandex-int/messenger.widget';
import * as IWidget from '@yandex-int/messenger.widget/lib_cjs/types';
import type { WidgetEvent } from '@yandex-int/messenger.widget/lib/libs/WidgetEvent/WidgetEvent';

import { isMeetingRoomEmbed } from '../../../../common/helpers';
import { getStore, RootState } from '../../../init-store';
import { getAppLang } from '../../../utils/common-i18n';
import { toggleChatVisibilityAction } from '../../pages/call/features/chat';
import { selectMessengerBackendPreset } from '../features/config/selectors';
import { getChatUserUid } from './get-chat-user-uid';
import { ACTION, COUNTERS, FEATURE } from './logger-enums';
import { createLogger } from './loggerFacade';
import { getMessengerOriginPreset } from './messenger-origin-preset';
import isOnPremise from './onpremise/is-onpremise';

type ChatData = {
    mountNode: HTMLElement;
    chatId: string;
    afterInit?: () => void;
};
export let widget: Widget | null = null;

const logger = createLogger('chat');

const getChatFlags = (state: RootState) => {
    return {
        calls: '0',
        disableDisplayRestriction: '1',
        disableChatHeader: '1',
        disableChatList: '1',
        disableNavigation: '1',
        hideClose: '1',
        notificationSuggest: '0',
        onboarding: '0',
        recommendedContacts: '0',
        recommended_chats: '0',
        visibilityMode: 'visible',
        voice: '0',
        webpush: '0',
        theme: 'dark',
        readOnly: isMeetingRoomEmbed(state.config.embed) ? '1' : undefined,
    } satisfies Partial<FlagsConfig>;
};

export const getWidgetLang = () => {
    const appLang = getAppLang();
    /**
     * Для украинского возвращаем русский, т.к. виджет не поддерживает украинский
     */
    if (appLang === 'uk') {
        return 'ru';
    }
    return appLang;
};

/**
 * См. https://arcanum.yandex-team.ru/arcadia/frontend/packages/messenger.widget/doc/api.md
 *
 * @param flag
 */
export const setChatVisibility = async (flag: boolean) => {
    await widget?.api.setVisibility({ visible: flag });
};

export const setChatFocus = async () => {
    await widget?.api.focus();
};

/**
 * Показывает чат по chatId
 *
 * @param params
 */
export const showChat = (params?: IWidget.IframeOpenData) => {
    widget?.show(params ?? {}, (error) => {
        if (error) {
            logger.endDelta(COUNTERS.OPEN_CHAT_DELTA, { status: 'failed' });
            logger.endFeatureDelta({ feature: FEATURE.CHAT, action: ACTION.OPEN, isSuccess: false });
            logger.error(error, {
                method: 'showChat',
                additional: {
                    widgetId: widget?.widgetId,
                },
            });
        } else {
            logger.endDelta(COUNTERS.OPEN_CHAT_DELTA, { status: 'success' });
            logger.endFeatureDelta({ feature: FEATURE.CHAT, action: ACTION.OPEN, isSuccess: true });
        }
    });
};

/**
 * Уничтожает виджет и все подписчики на события
 */
export const destroyChatWidget = () => {
    if (widget) {
        widget?.destroy();
        for (const key in widget.events) {
            const eventKey = key as keyof Widget['events'];
            widget?.events?.[eventKey]?.removeAllListener();
        }
        widget = null;
    }
};

const CHAT_DATA_ATTRIBUTE = 'messengerIframe';

export const isChatIframe = (element: HTMLElement) =>
    Boolean(element instanceof HTMLIFrameElement && element.dataset[CHAT_DATA_ATTRIBUTE]);

let authToken: string | undefined;

export const setChatWidgetAuthorizationToken = (token: string) => {
    authToken = `OAuth ${token}`;
};

class WidgetUI extends Block {
    init(...args: Parameters<Block['init']>) {
        super.init(...args);
        const context = this.context;

        context.onPropsChanged.addListener(({ value, prevValue }) => {
            if (value.iframe && value.iframe !== prevValue?.iframe) {
                value.iframe.dataset[CHAT_DATA_ATTRIBUTE] = 'true';
            }
        });
    }
}

/**
 * Создание экземпляра виджета мессенджера.
 * https://arcanum.yandex-team.ru/arcadia/frontend/packages/messenger.widget/doc
 *
 * @param mountNode - нода для монтирования
 * @param chatId - айди чата
 */
export const initChatWidget = async ({ mountNode, chatId }: ChatData): Promise<Maybe<Widget> | null> => {
    if (widget) {
        return widget;
    }

    const store = getStore();
    const state = store.getState();

    const environment = state.config.environment || '';
    const isTesting = ['development', 'testing'].includes(environment);
    const backendPresetDefault = isTesting ? presets.backend.test : presets.backend.prod;

    const backendPresetFromConfigKey = selectMessengerBackendPreset(state);
    const backendPresetFromConfig = backendPresetFromConfigKey && presets.backend[backendPresetFromConfigKey];
    const backendPreset = backendPresetFromConfig ?? backendPresetDefault;
    const originPreset = getMessengerOriginPreset(state);

    const userUid = getChatUserUid();

    try {
        const unreadCounter = yandexUnreadCounterFactory();
        const ui = new WidgetUI();
        widget = new Widget(
            new YandexSingleChatConfig({
                workspaceId: 'telemost',
                serviceId: 39, // telemost-web
                unreadCounterChatId: chatId,
                fullscreen: true,
                flags: getChatFlags(state),
                lang: getWidgetLang(),
                build: isOnPremise ? 'telemost-onpremises' : 'telemost',
                authToken,
                selectedUid: isTesting ? undefined : userUid,
                ...backendPreset,
                ...(originPreset ?? {}),
                // только для debug
                // ...presets.debug.all,
            })
        );

        widget.addPlugin(unreadCounter).setUI(ui);

        widget?.events.error.addListener((errorEvent: WidgetEvent<ClientApi.LegacyError>) => {
            const { message, code } = errorEvent?.data ?? {};
            logger.error(new Error(message), {
                method: code || 'unknown',
                additional: {
                    widgetId: widget?.widgetId,
                },
            });
        });

        widget?.events.chatLoadError.addListener((errorEvent: WidgetEvent<ClientApi.ChatLoadError>) => {
            const { error, additional = {} } = errorEvent?.data ?? {};
            const { message } = error ?? {};
            logger.error(new Error(message), {
                method: 'chatLoad',
                additional: {
                    widgetId: widget?.widgetId,
                    ...additional,
                },
            });
        });

        widget.init();
        ui.mount(mountNode);
        store.dispatch(toggleChatVisibilityAction(false));
        return widget;
    } catch (error) {
        logger.error(error, { block: 'chat', method: 'initChatWidget' });
        return Promise.reject();
    }
};
